import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, XCircle, FileDown, Loader2 } from "lucide-react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API, errMsg } from "@/lib/api";
import { COMPANY, LOGO } from "@/lib/constants";
import { sar, dmy } from "@/components/QuoteCard";

const Decided = ({ d }) => {
  const ok = d.status === "accepted";
  return (
    <div className="py-4 space-y-2 text-center" data-testid="quote-decided">
      {ok ? <CheckCircle2 size={48} className="mx-auto text-emerald-600" /> : <XCircle size={48} className="mx-auto text-red-600" />}
      <p className="font-heading font-bold text-lg">{ok ? "شكراً لموافقتك على العرض!" : "تم تسجيل رفضك للعرض"}</p>
      <p className="text-sm text-slate-500">{ok ? "سيتواصل معك فريقنا قريباً لترتيب الخطوات التالية." : "شكراً لوقتك، يسعدنا خدمتك في أي وقت."}</p>
    </div>
  );
};

const Items = ({ info }) => (
  <div className="text-sm border border-slate-200 rounded-xl overflow-hidden" data-testid="quote-public-items">
    {info.items.map((it, i) => (
      <div key={i} className="flex justify-between gap-3 px-3 py-2 border-b border-slate-100">
        <span className="text-start">{it.desc} <span className="text-slate-400 font-mono">×{it.qty}</span></span>
        <span className="font-mono whitespace-nowrap">{sar(it.total)}</span>
      </div>
    ))}
    <div className="px-3 py-2 bg-slate-50 space-y-1">
      <p className="flex justify-between"><span>المجموع قبل الضريبة</span><span className="font-mono">{sar(info.subtotal)}</span></p>
      <p className="flex justify-between"><span>الضريبة {Math.round(info.vat_rate * 100)}%</span><span className="font-mono">{sar(info.vat)}</span></p>
    </div>
    <p className="flex justify-between px-3 py-3 bg-brand-red text-white font-bold text-base"><span>الإجمالي شامل الضريبة</span><span data-testid="quote-public-total"><span className="font-mono">{sar(info.total)}</span> ر.س</span></p>
  </div>
);

export default function QuoteDecision() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("");
  const [name, setName] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/quotes/${token}/info`).then((r) => { setInfo(r.data); setName(r.data.customer_name); }).catch((e) => setError(errMsg(e)));
  }, [token]);

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/public/quotes/${token}/decision`, { decision: mode, name, reason });
      setInfo((x) => ({ ...x, decision: data.decision }));
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-mist flex items-center justify-center p-4" data-testid="quote-decision-page">
      <div className="w-full max-w-lg bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden">
        <div className="h-1.5 bg-brand-red" /><div className="h-1 bg-brand-navy" />
        <div className="p-6 space-y-5">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt={COMPANY} className="h-16 w-16 rounded-full object-contain" />
            <div><p className="font-heading font-extrabold text-lg text-brand-navy">{COMPANY}</p><p className="text-xs text-slate-500">عرض سعر مبدئي</p></div>
          </div>
          {!info && !error && <Loader2 className="animate-spin mx-auto text-brand-red" />}
          {!info && error && <p className="text-red-600 text-center" data-testid="quote-public-error">{error}</p>}
          {info && (
            <>
              <p className="text-sm text-slate-600">أهلاً {info.customer_name}، هذا عرض السعر المبدئي رقم <bdi dir="ltr" className="font-mono font-bold whitespace-nowrap inline-block">{info.number}</bdi> بناءً على المعاينة التي قام بها {info.agent_name}.</p>
              <Items info={info} />
              <p className="text-xs text-slate-500">صالح حتى {dmy(info.valid_until)}{info.notes && ` · ${info.notes}`}</p>
              <a href={`${API}/public/quotes/${token}`} target="_blank" rel="noreferrer" data-testid="quote-public-pdf"
                className="h-11 rounded-lg border border-slate-200 font-semibold text-sm flex items-center justify-center gap-2 hover:bg-slate-50"><FileDown size={16} />عرض ملف PDF</a>
              {info.decision ? <Decided d={info.decision} /> : info.expired ? (
                <p className="text-center text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm" data-testid="quote-public-expired">انتهت صلاحية هذا العرض، تواصل معنا لعرض محدّث.</p>
              ) : !mode ? (
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => setMode("accepted")} data-testid="quote-accept-button" className="h-12 rounded-lg bg-emerald-600 text-white font-bold flex items-center justify-center gap-2 hover:bg-emerald-700 transition-colors"><CheckCircle2 size={18} />قبول العرض</button>
                  <button onClick={() => setMode("rejected")} data-testid="quote-reject-button" className="h-12 rounded-lg border border-red-300 text-red-700 font-bold flex items-center justify-center gap-2 hover:bg-red-50 transition-colors"><XCircle size={18} />رفض العرض</button>
                </div>
              ) : (
                <div className="space-y-3 rounded-xl border border-slate-200 p-4" data-testid="quote-confirm-box">
                  <p className="font-bold">{mode === "accepted" ? "تأكيد قبول العرض" : "تأكيد رفض العرض"}</p>
                  <Input value={name} onChange={(e) => setName(e.target.value.slice(0, 100))} placeholder="اسمك" data-testid="quote-decision-name" />
                  {mode === "rejected" && <Textarea value={reason} onChange={(e) => setReason(e.target.value.slice(0, 500))} placeholder="سبب الرفض (اختياري)" data-testid="quote-decision-reason" />}
                  {error && <p className="text-sm text-red-600" data-testid="quote-public-error">{error}</p>}
                  <div className="flex gap-2">
                    <button onClick={submit} disabled={busy} data-testid="quote-decision-submit"
                      className={`flex-1 h-12 rounded-lg text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60 ${mode === "accepted" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}`}>
                      {busy && <Loader2 size={18} className="animate-spin" />}تأكيد
                    </button>
                    <button onClick={() => { setMode(""); setError(""); }} data-testid="quote-decision-cancel" className="h-12 px-4 rounded-lg border border-slate-200 font-semibold hover:bg-slate-50">رجوع</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
